import { useEffect, useRef } from 'react';
import { STROKE, pinchScale, recognise } from '../utils/gestures/recognise';

// Where a stroke never starts: what takes a finger's drag for itself.
const IGNORE = 'input, textarea, select, [contenteditable="true"], [role="slider"], [data-gesture="off"]';
// Where a pinch never starts, besides: a sheet or a dialog over the picture.
const NO_PINCH = `${IGNORE}, [role="dialog"]`;
// How long after a swipe its own click is swallowed.
const SWALLOW_MS = 500;

/**
 * A camera's strokes, read and acted on (2026-10-21; docs/FIELD_AI_VISION_SPEC.md
 * section 8). Pointer events are taken on `ref`'s element in the capture
 * phase, so a swipe may start over a chip or a bar button as well as over the
 * picture; each stroke goes to utils/gestures/recognise.js when the finger
 * lifts, and an edge swipe or a loop is handed on.
 *
 *   - One finger only: a second finger landing drops the stroke. Two
 *     fingers are a pinch, handed to `onPinch` where a host gives one (the
 *     camera's zoom, 2026-10-22): {phase: 'start'|'move'|'end', scale}, the
 *     spread against where the fingers landed (recognise.js pinchScale).
 *     Neither finger may start on what IGNORE names or in a sheet; a third
 *     changes nothing; the click a pinch may leave behind is swallowed.
 *     `pinch` (default: `enabled`) says when it is read: Field Mode zooms
 *     with its non-modal controls sheet open too.
 *   - A stroke that starts in a text field, a slider or anything marked
 *     `data-gesture="off"` (the photo tray, which scrolls) is left alone.
 *   - A tap is not handled here: it stays the click it always was, so a tap
 *     on the picture still focuses there (useTapFocus) and a button still
 *     presses. A swipe that began on a button swallows the click the browser
 *     may send after it, so pulling a sheet up from the bar takes no photo:
 *     only that click, on what the swipe began on, and only until the next
 *     touch (the sheet it opened is in here too, and its x must press).
 *   - `enabled` false (a sheet, the summary or a dialog open) reads nothing.
 *
 * A page uses it too (2026-10-25, components/Layout.js: the hubs' page swipe,
 * D12): `swipes` are the directions read as a swipe across ('right' = left to
 * right, physical), handed to `onSwipe(direction)`; `pointerTypes` limits the
 * strokes read to those devices (a page: touch and pen, never a mouse);
 * `startsOn(target, event)` may refuse a stroke where it starts
 * (utils/gestures/arbitrate.js: a card's own swipe, the system's edges);
 * `rules` are recognise.js's thresholds, stricter for a page.
 */
export default function useViewportGestures(ref, {
  enabled = true, edges = [], lasso = false, onEdgeSwipe, onLasso, pinch = enabled, onPinch,
  swipes = [], onSwipe, pointerTypes = null, startsOn = null, rules = STROKE,
} = {}) {
  const latest = useRef(null);
  latest.current = {
    enabled, edges, lasso, onEdgeSwipe, onLasso, pinch, onPinch, swipes, onSwipe, pointerTypes, startsOn, rules,
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const down = new Set();
    let stroke = null;
    // Where each finger down is now, and whether it may be half of a pinch.
    const at = new Map();
    // The pinch under way: {ids, start: [two points]}.
    let pinching = null;
    // The click a finished swipe may still bring: on what it began on, soon
    // (a pinch's: anything, soon).
    let swallow = null;
    const point = (e, rect) => ({ x: e.clientX - rect.left, y: e.clientY - rect.top, t: e.timeStamp });
    const spreadOf = ids => ids.map(id => at.get(id));
    const endPinch = () => {
      if (!pinching) return;
      const done = pinching;
      pinching = null;
      swallow = { target: null, until: Date.now() + SWALLOW_MS };
      latest.current.onPinch?.({ phase: 'end', scale: pinchScale(done.start, spreadOf(done.ids)) });
    };

    const onDown = (e) => {
      swallow = null;
      down.add(e.pointerId);
      at.set(e.pointerId, { x: e.clientX, y: e.clientY, free: !e.target?.closest?.(NO_PINCH) });
      if (down.size > 1) {
        stroke = null;
        const now = latest.current;
        const ids = [...down];
        if (down.size === 2 && !pinching && now.pinch && now.onPinch && ids.every(id => at.get(id)?.free)) {
          pinching = { ids, start: spreadOf(ids) };
          now.onPinch({ phase: 'start', scale: 1 });
        }
        return;
      }
      if (!latest.current.enabled) return;
      if (latest.current.pointerTypes && !latest.current.pointerTypes.includes(e.pointerType)) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target?.closest?.(IGNORE)) return;
      if (latest.current.startsOn && !latest.current.startsOn(e.target, e)) return;
      const rect = el.getBoundingClientRect();
      stroke = { id: e.pointerId, rect, target: e.target, points: [point(e, rect)] };
    };
    const onMove = (e) => {
      if (at.has(e.pointerId)) at.set(e.pointerId, { ...at.get(e.pointerId), x: e.clientX, y: e.clientY });
      if (pinching && pinching.ids.includes(e.pointerId)) {
        latest.current.onPinch?.({ phase: 'move', scale: pinchScale(pinching.start, spreadOf(pinching.ids)) });
        return;
      }
      if (stroke && e.pointerId === stroke.id) stroke.points.push(point(e, stroke.rect));
    };
    const onUp = (e) => {
      if (pinching && pinching.ids.includes(e.pointerId)) endPinch();
      down.delete(e.pointerId);
      at.delete(e.pointerId);
      if (!stroke || e.pointerId !== stroke.id) return;
      const done = stroke;
      stroke = null;
      done.points.push(point(e, done.rect));
      const now = latest.current;
      if (!now.enabled) return;
      const result = recognise(done.points, { width: done.rect.width, height: done.rect.height },
        { edges: now.edges, swipes: now.swipes, lasso: now.lasso, rules: now.rules });
      if (result.kind === 'edgeSwipe') {
        swallow = { target: done.target, until: Date.now() + SWALLOW_MS };
        now.onEdgeSwipe?.(result.edge);
      } else if (result.kind === 'swipe') {
        swallow = { target: done.target, until: Date.now() + SWALLOW_MS };
        now.onSwipe?.(result.direction);
      } else if (result.kind === 'lasso') {
        swallow = { target: done.target, until: Date.now() + SWALLOW_MS };
        now.onLasso?.(result.loop, { width: done.rect.width, height: done.rect.height });
      }
    };
    const onCancel = (e) => {
      if (pinching && pinching.ids.includes(e.pointerId)) endPinch();
      down.delete(e.pointerId);
      at.delete(e.pointerId);
      if (stroke && e.pointerId === stroke.id) stroke = null;
    };
    const onClick = (e) => {
      if (!swallow) return;
      const { target, until } = swallow;
      swallow = null;
      // A browser's click after a drag lands on what it began on, or on
      // something holding both ends of it.
      const same = target === null
        || (target && (e.target === target || target.contains?.(e.target) || e.target?.contains?.(target)));
      if (Date.now() >= until || !same) return;
      e.preventDefault();
      e.stopPropagation();
    };

    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerup', onUp, true);
    el.addEventListener('pointercancel', onCancel, true);
    el.addEventListener('click', onClick, true);
    return () => {
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerup', onUp, true);
      el.removeEventListener('pointercancel', onCancel, true);
      el.removeEventListener('click', onClick, true);
    };
  }, [ref]);
}
